import type { SortingState } from '@tanstack/react-table';
import {
    Icon,
    Table,
    TableBody,
    TableCell,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
} from '@ksyos/design-system';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type { FormattedOutpatientClinicTask } from '@frontend/components/pages/outpatientClinic/types/formattedOutpatientClinicTask';
import { SortDirection } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { useTableSelection } from '../hooks/useTableSelection';
import type { OutpatientClinicCategory } from '../types/outpatientClinicCategory';
import { useNavigateWithEmployment } from '../hooks/useNavigateWithEmployment';
import { getEmploymentIdFromOrganizationId } from '../utils/getEmploymentIdFromOrganizationId';
import { getOutpatientClinicTaskUrl } from '../utils/getOutpatientClinicTaskUrl';
import { translateOutpatientClinicCategory } from '../utils/translateOutpatientClinicCategory';

const columnHelper = createColumnHelper<FormattedOutpatientClinicTask>();

export type OutpatientClinicTaskListTableProps = {
    onSortingChange: (updater: SortingState | ((old: SortingState) => SortingState)) => void;
    sorting: SortingState;
    tableSelection: ReturnType<typeof useTableSelection<string>>;
    tasks: FormattedOutpatientClinicTask[];
    total: number;
};

export function OutpatientClinicTaskListTable({
    tasks,
    onSortingChange,
    sorting,
    tableSelection,
}: Readonly<OutpatientClinicTaskListTableProps>) {
    const translate = getSelectedLanguage();
    const abortSignal = useAbortSignal();
    const navigateWithEmployment = useNavigateWithEmployment();

    const { isSelected, toggle } = tableSelection;

    const columns = [
        columnHelper.accessor('id', {
            enableSorting: false,
            header: '',
        }),
        columnHelper.accessor('expectedDateCompleted', {
            cell: (cell) => {
                if (cell.row.original.isOverdue) {
                    return (
                        <Text
                            as="span"
                            color="danger"
                            size="sm"
                            sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}
                            whiteSpace="nowrap"
                        >
                            {cell.getValue()}
                            <Icon
                                color="danger"
                                icon="warning"
                                sx={{ flexShrink: 0, ml: 2, mr: 4 }}
                            />
                        </Text>
                    );
                }

                return cell.getValue();
            },
            enableSorting: true,
            header: translate.contactsAndTasks.expectedDateCompleted,
        }),
        columnHelper.accessor('availability', {
            cell: (cell) => {
                if (cell.row.original.availability?.firstTimeRange || cell.row.original.availability?.secondTimeRange) {
                    return (
                        <>
                            {cell.row.original.availability.firstTimeRange && (
                                <Text
                                    as="span"
                                    size="sm"
                                    sx={{ display: 'block' }}
                                    whiteSpace="nowrap"
                                >
                                    {cell.row.original.availability.firstTimeRange}
                                </Text>
                            )}
                            {cell.row.original.availability.secondTimeRange && (
                                <Text
                                    as="span"
                                    size="sm"
                                    sx={{ display: 'block' }}
                                    whiteSpace="nowrap"
                                >
                                    {cell.row.original.availability.secondTimeRange}
                                </Text>
                            )}
                        </>
                    );
                }

                return '-';
            },
            enableSorting: false,
            header: translate.contactsAndTasks.availability,
        }),
        columnHelper.accessor('patientName', {
            enableSorting: false,
            header: translate.contactsAndTasks.patientTaskList.patientName,
        }),
        columnHelper.accessor('birthDate', {
            enableSorting: false,
            header: translate.patient.birthDateShort,
        }),
        columnHelper.accessor('category.name', {
            cell: (cell) => translateOutpatientClinicCategory(cell.getValue<OutpatientClinicCategory>()),
            enableSorting: false,
            header: translate.outpatientClinic.tasks.category,
        }),
        columnHelper.accessor('description', {
            enableSorting: false,
            header: translate.request.taskType,
        }),
        columnHelper.accessor('episodeCategory', {
            cell: (cell) =>
                translate.episodeCategoryEnum[cell.getValue().name as keyof typeof translate.episodeCategoryEnum],
            enableSorting: false,
            header: translate.common.episodeCategory,
        }),
        columnHelper.accessor('performerPractitioner', {
            cell: (cell) => {
                if (cell.row.original.performerPractitioner || cell.row.original.performerPractitionerProfession) {
                    return (
                        <>
                            {cell.row.original.performerPractitioner && (
                                <Text
                                    as="span"
                                    fontWeight="medium"
                                    size="sm"
                                    sx={{ display: 'block' }}
                                    whiteSpace="nowrap"
                                >
                                    {cell.row.original.performerPractitioner}
                                </Text>
                            )}
                            {cell.row.original.performerPractitionerProfession ? (
                                <Text
                                    as="span"
                                    size="sm"
                                    sx={{ display: 'block' }}
                                    whiteSpace="nowrap"
                                >
                                    {cell.row.original.performerPractitionerProfession}
                                </Text>
                            ) : (
                                '-'
                            )}
                        </>
                    );
                }

                return '-';
            },
            enableSorting: false,
            header: translate.contactsAndTasks.patientTaskList.performerPractitioner,
        }),
    ];

    const { getHeaderGroups, getRowModel } = useReactTable({
        columns,
        data: tasks,
        getCoreRowModel: getCoreRowModel(),
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        onSortingChange,
        state: {
            sorting,
        },
    });

    return (
        <TableContainer>
            <Table spacing="medium">
                <TableHead>
                    {getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <TableHeader
                                    isSortable={header.column.getCanSort()}
                                    isSortHeader={header.column.getIsSorted() !== false}
                                    key={header.id}
                                    onClick={header.column.getToggleSortingHandler()}
                                    sortDirection={
                                        header.column.getIsSorted() !== false
                                            ? header.column.getIsSorted() === 'asc'
                                                ? SortDirection.ascending
                                                : SortDirection.descending
                                            : undefined
                                    }
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHeader>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody>
                    {getRowModel().rows.map((row) => (
                        <TableRow
                            key={row.id}
                            onClick={async () => await handleClick(row.original)}
                        >
                            {row.getVisibleCells().map((cell) =>
                                cell.column.id === 'id' ? (
                                    <TableCellSelectRow
                                        isSelected={isSelected(cell.row.original.id)}
                                        key={cell.id}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            toggle(cell.row.original.id);
                                        }}
                                    />
                                ) : (
                                    <TableCell key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                )
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    async function handleClick(patientTask: FormattedOutpatientClinicTask) {
        const url = getOutpatientClinicTaskUrl(patientTask);
        const employmentId = await getEmploymentIdFromOrganizationId(
            patientTask.performerOrganization.id ?? patientTask.creatorOrganization.id,
            abortSignal
        );
        await navigateWithEmployment(url, employmentId);
    }
}
